/**
 * Tag validation utilities for consistent tag input validation across the platform
 *
 * Provides centralized tag validation logic used by Assets, Technologies, Seeds,
 * and Vulnerabilities sections. Ensures consistent validation rules and error
 * messages across all entity types.
 *
 * Validation Rules:
 * - Length: 1-50 characters
 * - Characters: Alphanumeric, hyphens, underscores, dots, colons
 * - Start: Must start with alphanumeric character
 * - Whitespace: Trimmed automatically
 * - Case: Case-sensitive (preserves user input)
 *
 * @example
 * ```typescript
 * const result = validateTagInput('production-tag');
 * if (!result.isValid) {
 *   toast.error(result.error);
 *   return;
 * }
 * // Use result.sanitized
 * ```
 */

/**
 * Result type for tag validation operations
 */
export interface TagValidationResult {
  /** Whether the tag input is valid */
  isValid: boolean;
  /** User-friendly error message if validation fails */
  error?: string;
  /** Sanitized tag value ready for use */
  sanitized?: string;
}

// Tag validation constraints
const TAG_MIN_LENGTH = 1;
const TAG_MAX_LENGTH = 50;

/**
 * Pattern for valid tag characters:
 * - Must start with alphanumeric (letter or number)
 * - Can contain alphanumeric, dots, hyphens, underscores, colons
 * - No special characters like @#$%^&*()
 */
const TAG_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9._:-]*$/;

/**
 * Validates tag input according to platform rules
 *
 * Performs comprehensive validation including sanitization, length checks,
 * and character pattern validation. Returns detailed validation result
 * with user-friendly error messages.
 *
 * @param tag - Raw tag input from user
 * @returns Validation result with sanitized value and any error
 *
 * @example
 * // Valid tags
 * validateTagInput('production')     // { isValid: true, sanitized: 'production' }
 * validateTagInput('  staging  ')    // { isValid: true, sanitized: 'staging' }
 * validateTagInput('api-v2')         // { isValid: true, sanitized: 'api-v2' }
 * validateTagInput('env_prod')       // { isValid: true, sanitized: 'env_prod' }
 * validateTagInput('web.app.v1')     // { isValid: true, sanitized: 'web.app.v1' }
 *
 * @example
 * // Invalid tags
 * validateTagInput('')               // { isValid: false, error: 'Tag cannot be empty' }
 * validateTagInput('   ')            // { isValid: false, error: 'Tag cannot be empty' }
 * validateTagInput('a'.repeat(51))   // { isValid: false, error: 'Tag cannot exceed 50 characters' }
 * validateTagInput('tag@special')    // { isValid: false, error: 'Tag contains invalid characters...' }
 * validateTagInput('-starts-dash')   // { isValid: false, error: 'Tag must start with a letter or number' }
 */
export function validateTagInput(tag: string): TagValidationResult {
  // Step 1: Sanitize the input
  const sanitized = sanitizeTag(tag);

  // Step 2: Check if empty after sanitization
  if (sanitized.length === 0) {
    return {
      isValid: false,
      error: 'Tag cannot be empty',
    };
  }

  // Step 3: Check minimum length
  if (sanitized.length < TAG_MIN_LENGTH) {
    return {
      isValid: false,
      error: `Tag must be at least ${TAG_MIN_LENGTH} character${TAG_MIN_LENGTH > 1 ? 's' : ''} long`,
    };
  }

  // Step 4: Check maximum length
  if (sanitized.length > TAG_MAX_LENGTH) {
    return {
      isValid: false,
      error: `Tag cannot exceed ${TAG_MAX_LENGTH} characters`,
    };
  }

  // Step 5: Check character pattern
  if (!TAG_PATTERN.test(sanitized)) {
    // Determine specific error message based on what's wrong
    if (!/^[a-zA-Z0-9]/.test(sanitized)) {
      return {
        isValid: false,
        error: 'Tag must start with a letter or number',
      };
    }

    return {
      isValid: false,
      error:
        'Tag contains invalid characters. Use only letters, numbers, dots, hyphens, underscores, and colons',
    };
  }

  // Step 6: All validations passed
  return {
    isValid: true,
    sanitized,
  };
}

/**
 * Sanitizes tag input by trimming whitespace and normalizing
 *
 * Removes leading and trailing whitespace while preserving the
 * original case of the tag. Does not modify internal structure
 * or characters.
 *
 * Note: Tags are case-sensitive to allow for different conventions
 * (e.g., 'Production' vs 'production' may have different meanings
 * in user's workflow)
 *
 * @param tag - Raw tag input
 * @returns Sanitized tag string with whitespace removed
 *
 * @example
 * sanitizeTag('  production  ')    // 'production'
 * sanitizeTag('API-Gateway')       // 'API-Gateway' (preserves case)
 * sanitizeTag('tag')               // 'tag'
 */
export function sanitizeTag(tag: string): string {
  if (typeof tag !== 'string') {
    return '';
  }

  // Trim leading and trailing whitespace
  // Preserve original case for user flexibility
  return tag.trim();
}

/**
 * Quick boolean check if tag is valid
 *
 * Convenience method for simple validation checks without
 * needing detailed error messages. Use validateTagInput()
 * when you need error details for user feedback.
 *
 * @param tag - Tag to validate
 * @returns True if valid, false otherwise
 *
 * @example
 * if (!isValidTag(userInput)) {
 *   // Show generic error
 *   return;
 * }
 *
 * @example
 * // When you need specific errors, use validateTagInput instead:
 * const result = validateTagInput(userInput);
 * if (!result.isValid) {
 *   toast.error(result.error);
 * }
 */
export function isValidTag(tag: string): boolean {
  return validateTagInput(tag).isValid;
}

/**
 * Get user-friendly validation error message
 *
 * Extracts error message from validation result, returning
 * empty string if tag is valid. Useful for conditional
 * error display in UI components.
 *
 * @param tag - Tag that failed validation
 * @returns Error message or empty string if valid
 *
 * @example
 * const error = getTagValidationError(userInput);
 * if (error) {
 *   toast.error(error);
 * }
 *
 * @example
 * // Preferred approach for most cases:
 * const result = validateTagInput(userInput);
 * if (!result.isValid) {
 *   toast.error(result.error);
 * }
 */
export function getTagValidationError(tag: string): string {
  const result = validateTagInput(tag);
  return result.error || '';
}
